/**
 * `LiveReviewWizard` — wizard di review skill condotta dal vivo su un candidato
 * esterno (`ExternalCvProfileDoc`), aperto da un valutatore autenticato con
 * Levels_Access durante un colloquio.
 *
 * Riusa i pattern consolidati del progetto:
 *  - i **tre controlli di filtro** di `SkillAssessmentWizard` (ambito con
 *    optgroup reparti/professioni, tipologia e-CF + "senza tipo", ricerca
 *    testuale sul nome), ma l'applicazione dei criteri è delegata alla funzione
 *    **pura** `filterLiveReviewSkills` (vedi `liveReviewFilter.ts`), così la
 *    logica di filtro resta isolata e testabile (task 8.1/8.2);
 *  - lo schema **bozza/baseline** di `ReviewPanel` (una `Map<skillId, Livello |
 *    null>`), qui gestito dal riduttore puro `liveReviewDraftReducer`
 *    (`liveReviewDraft.ts`, task 8.3): assegnare un Livello registra il
 *    Review_Score, l'invio spedisce le sole skill votate (`livello !== null`);
 *  - il **precaricamento** via `GET` e l'**invio batch** via `POST` con
 *    invalidazione delle query e gestione errori tramite `Alert`.
 *
 * Differenze rispetto alla review interna:
 *  - un **unico `<textarea>`** per la `Review_Note` dell'intera review (non per
 *    skill);
 *  - il selettore di Livello offre **solo** la scala `e-1..e-5` (nessun `e-0`),
 *    coerente con la regola di dominio del backend (`isLivello(x) && x !==
 *    'e-0'`).
 *
 * Il componente è presentazionale/controllato: la visibilità è gestita dal
 * chiamante (`open`/`onClose`) e riceve il `profileId` del candidato. È così
 * montabile/apribile come wizard dal dettaglio candidato (task 10.1).
 *
 * _Requirements: 2.1, 2.3, 2.5, 3.1, 3.2, 3.3, 3.4, 4.1, 4.2, 4.5_
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CompetenceDto,
  CreateLiveReviewRequest,
  DepartmentDto,
  JobDto,
  LiveReviewSubmissionDto,
  Livello,
  SkillDto,
} from '../shared/dto';
import {
  LIVELLO_LABELS,
  LIVELLO_VALUES_EVALUABLE,
  SKILL_TYPE_LABELS,
  SKILL_TYPE_VALUES,
} from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import {
  Alert,
  Button,
  Field,
  Loading,
  Modal,
  TextArea,
  TextInput,
} from '../components/ui';
import {
  DEPT_PREFIX,
  JOB_PREFIX,
  filterLiveReviewSkills,
  type ScopeFilter,
  type TypeFilter,
} from './liveReviewFilter';
import {
  draftToScores,
  liveReviewDraftReducer,
  type LiveReviewDraft,
} from './liveReviewDraft';

/** Query keys locali del wizard. */
const QK = {
  skills: ['skills'] as const,
  competences: ['competences'] as const,
  departments: ['departments'] as const,
  jobs: ['jobs'] as const,
};

/** Chiave di precaricamento dell'ultima review del richiedente verso il profilo. */
const myLiveReviewKey = (profileId: string) =>
  ['externalCv', 'profiles', 'live-review', 'mine', profileId] as const;

/** Chiave della dashboard del profilo (da invalidare dopo l'invio). */
const profileDashboardKey = (profileId: string) =>
  ['externalCv', 'profiles', 'dashboard', profileId] as const;

export interface LiveReviewWizardProps {
  /** Candidato esterno oggetto della review. */
  profileId: string;
  /** Visibilità del wizard (controllata dal chiamante). */
  open: boolean;
  /** Richiesta di chiusura (Escape, backdrop, pulsante Chiudi). */
  onClose: () => void;
}

/**
 * Selettore controllato del Livello per la review: offre **solo** la scala
 * `e-1..e-5` (nessun `e-0`). Non riusa `LivelloSelect`, che include `e-0` dalla
 * scala completa `LIVELLO_VALUES`; qui l'`e-0` ("Nessuno") non è un voto
 * ammesso. Mantiene lo stesso stile e l'opzione placeholder non selezionabile.
 */
function ReviewLivelloSelect({
  value,
  onChange,
  label,
  disabled = false,
}: {
  value: Livello | null;
  onChange: (value: Livello) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <select
      className="livello-select"
      aria-label={label}
      value={value ?? ''}
      disabled={disabled}
      data-empty={value === null || undefined}
      onChange={(event) => {
        const next = event.target.value;
        // Accetta solo i Livelli ammessi come voto (`e-1..e-5`).
        if ((LIVELLO_VALUES_EVALUABLE as readonly string[]).includes(next)) {
          onChange(next as Livello);
        }
      }}
    >
      {/* Placeholder dello stato "non votato" (`null`), non selezionabile. */}
      <option value="" disabled>
        Seleziona un livello…
      </option>
      {LIVELLO_VALUES_EVALUABLE.map((livello) => (
        <option key={livello} value={livello}>
          {LIVELLO_LABELS[livello]}
        </option>
      ))}
    </select>
  );
}

/**
 * Wizard di compilazione e invio di una review skill dal vivo.
 */
export function LiveReviewWizard({ profileId, open, onClose }: LiveReviewWizardProps) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  // ----- Cataloghi (stessi del wizard di autovalutazione) -----

  // Catalogo completo delle skill: base del filtro e sorgente della tipologia.
  const skillsQuery = useQuery<SkillDto[]>({
    queryKey: QK.skills,
    queryFn: () => api.get<SkillDto[]>('/api/skills'),
    enabled: open,
  });

  // Competenze: anello che collega le skill a reparti (via `departmentId`) e a
  // professioni (via `JobDto.competenceIds`).
  const competencesQuery = useQuery<CompetenceDto[]>({
    queryKey: QK.competences,
    queryFn: () => api.get<CompetenceDto[]>('/api/competences'),
    enabled: open,
  });

  // Reparti (business unit): id → nome per l'optgroup "Per business unit".
  const departmentsQuery = useQuery<DepartmentDto[]>({
    queryKey: QK.departments,
    queryFn: () => api.get<DepartmentDto[]>('/api/departments'),
    enabled: open,
  });

  // Professioni (job): forniscono le competenze richieste per l'optgroup
  // "Per professione".
  const jobsQuery = useQuery<JobDto[]>({
    queryKey: QK.jobs,
    queryFn: () => api.get<JobDto[]>('/api/jobs'),
    enabled: open,
  });

  // Ultima review del richiedente verso il profilo (precaricamento): popola
  // bozza e baseline da `scores`/`note`, oppure stato vuoto se `null`.
  const myReviewQuery = useQuery<LiveReviewSubmissionDto | null>({
    queryKey: myLiveReviewKey(profileId),
    queryFn: () =>
      api.get<LiveReviewSubmissionDto | null>(
        `/api/external-cv/profiles/${encodeURIComponent(profileId)}/live-review/mine`,
      ),
    enabled: open && profileId !== '',
  });

  const skills = useMemo(() => skillsQuery.data ?? [], [skillsQuery.data]);
  const competences = useMemo(
    () => competencesQuery.data ?? [],
    [competencesQuery.data],
  );
  const jobs = useMemo(() => jobsQuery.data ?? [], [jobsQuery.data]);

  // ----- Controlli di filtro -----

  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  // Reparti con almeno una competenza collegata a una skill, per l'optgroup
  // "Per business unit" (le voci vuote non aiutano a filtrare).
  const departmentOptions = useMemo(() => {
    const nameById = new Map(
      (departmentsQuery.data ?? []).map((d) => [d.id, d.name]),
    );
    const withSkills = new Set<string>();
    for (const c of competences) {
      if (c.departmentId !== null && c.skillIds.length > 0) {
        withSkills.add(c.departmentId);
      }
    }
    const collator = new Intl.Collator('it', { sensitivity: 'base' });
    return [...withSkills]
      .filter((id) => nameById.has(id))
      .map((id) => ({ id, name: nameById.get(id) as string }))
      .sort((a, b) => collator.compare(a.name, b.name));
  }, [competences, departmentsQuery.data]);

  // Professioni con almeno una competenza, per l'optgroup "Per professione".
  const jobOptions = useMemo(() => {
    const collator = new Intl.Collator('it', { sensitivity: 'base' });
    return jobs
      .filter((j) => j.competenceIds.length > 0)
      .map((j) => ({ id: j.id, name: j.name }))
      .sort((a, b) => collator.compare(a.name, b.name));
  }, [jobs]);

  // Skill visibili: applica i criteri attivi tramite la funzione pura condivisa.
  const visibleSkills = useMemo(
    () =>
      filterLiveReviewSkills(
        { skills, competences, jobs },
        { scope: scopeFilter, type: typeFilter, search },
      ),
    [skills, competences, jobs, scopeFilter, typeFilter, search],
  );

  // ----- Bozza/baseline (schema di ReviewPanel, riduttore puro) -----

  // Bozza dei Review_Score in composizione (skillId → Livello votato o `null`).
  // Le transizioni passano per il riduttore puro `liveReviewDraftReducer`
  // (task 8.3), che scarta i Livelli fuori scala e fa prevalere l'ultima
  // assegnazione per skill. Il precaricamento imposta direttamente la Map.
  const [draft, setDraft] = useState<LiveReviewDraft>(() => new Map());

  // Assegna un Livello a una skill applicando il riduttore puro della bozza.
  const assignLivello = (skillId: string, livello: Livello) => {
    setDraft((prev) =>
      liveReviewDraftReducer(prev, { type: 'assign', skillId, livello }),
    );
  };
  // Nota libera dell'intera review (unico `<textarea>`).
  const [note, setNote] = useState('');
  // Stato precaricato (baseline): rileva se la review è stata modificata, così
  // l'invio si abilita solo dopo un cambiamento reale.
  const [baselineScores, setBaselineScores] = useState<Map<string, Livello>>(
    new Map(),
  );
  const [baselineNote, setBaselineNote] = useState('');

  // Precarica bozza, nota e baseline dall'ultima review del richiedente quando
  // i dati sono pronti. Se non esiste alcuna review (`null`), lo stato è vuoto;
  // i campi restano comunque modificabili (Req. 3.1, 3.2, 3.5).
  useEffect(() => {
    if (!open || !myReviewQuery.isSuccess) return;

    const previous = myReviewQuery.data;
    const next: LiveReviewDraft = new Map();
    const base = new Map<string, Livello>();
    for (const s of previous?.scores ?? []) {
      next.set(s.skillId, s.livello);
      base.set(s.skillId, s.livello);
    }
    const loadedNote = previous?.note ?? '';

    setDraft(next);
    setBaselineScores(base);
    setNote(loadedNote);
    setBaselineNote(loadedNote);
  }, [open, myReviewQuery.isSuccess, myReviewQuery.data]);

  // Invio della review come submission datata unica.
  const submit = useMutation({
    mutationFn: (body: CreateLiveReviewRequest) =>
      api.post<LiveReviewSubmissionDto>(
        `/api/external-cv/profiles/${encodeURIComponent(profileId)}/live-review`,
        body,
      ),
    onSuccess: () => {
      // Il precaricamento e la dashboard del profilo vanno riletti dopo l'invio.
      void queryClient.invalidateQueries({ queryKey: myLiveReviewKey(profileId) });
      void queryClient.invalidateQueries({
        queryKey: profileDashboardKey(profileId),
      });
      // Invio riuscito: chiude il wizard.
      onClose();
    },
  });

  // Review_Score effettivamente votati nella bozza corrente.
  const votedScores = useMemo(() => draftToScores(draft), [draft]);
  const votedCount = votedScores.length;

  // `true` se la bozza o la nota differiscono dal precaricamento: l'invio si
  // abilita solo dopo una modifica reale (Req. 3.3 — nuova submission a seguito
  // di una modifica).
  const isDirty = useMemo(() => {
    if (note.trim() !== baselineNote.trim()) return true;
    if (votedScores.length !== baselineScores.size) return true;
    for (const { skillId, livello } of votedScores) {
      if (baselineScores.get(skillId) !== livello) return true;
    }
    return false;
  }, [note, baselineNote, votedScores, baselineScores]);

  // `true` se la review ha un contenuto: almeno un voto oppure una nota non
  // vuota. Una review completamente vuota non è inviabile.
  const hasContent = votedScores.length > 0 || note.trim() !== '';

  const handleSubmit = () => {
    // Invia le skill votate (Livello non `null`) e la nota normalizzata. È
    // ammesso inviare la sola nota (nessuna skill votata).
    if (!hasContent) return;
    const trimmedNote = note.trim();
    submit.mutate({
      scores: votedScores,
      note: trimmedNote === '' ? null : trimmedNote,
    });
  };

  const isLoadingData =
    skillsQuery.isLoading ||
    competencesQuery.isLoading ||
    myReviewQuery.isLoading;
  const isDataError =
    skillsQuery.isError || competencesQuery.isError || myReviewQuery.isError;
  const dataReady =
    skillsQuery.isSuccess && competencesQuery.isSuccess && myReviewQuery.isSuccess;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Review skill dal vivo"
      size="wide"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Chiudi
          </Button>
          <Button
            type="submit"
            form="live-review-form"
            variant="primary"
            disabled={submit.isPending || !hasContent || !isDirty}
          >
            {submit.isPending ? 'Invio…' : 'Invia review'}
          </Button>
        </>
      }
    >
      {isLoadingData && <Loading>Caricamento della review…</Loading>}
      {isDataError && (
        <Alert tone="error">Impossibile caricare i dati della review.</Alert>
      )}

      {dataReady && (
        <form
          id="live-review-form"
          className="review-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {/* Unico campo Review_Note di testo libero per l'intera review. */}
          <Field id="live-review-note" label="Nota della review">
            <TextArea
              id="live-review-note"
              rows={6}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Annotazioni sulla review…"
              disabled={submit.isPending}
            />
          </Field>

          {/* Tabella delle skill: un titoletto "Review skill" sopra la riga di
              filtri/ricerca (ambito, tipologia, ricerca testuale) che fa da
              intestazione. */}
          <div className="live-review-form__skills">
            <h3 className="live-review-form__skills-title">Review skill</h3>
            <div className="assessment-wizard__skills-header live-review-form__filters">
              <div className="competence-search">
                <span className="competence-search__department user-admin__select">
                  <select
                    id="live-review-scope"
                    aria-label="Filtra le skill per ambito"
                    value={scopeFilter}
                    onChange={(e) => setScopeFilter(e.target.value)}
                  >
                    <option value="all">Tutte le skill</option>
                    {departmentOptions.length > 0 && (
                      <optgroup label="Per business unit">
                        {departmentOptions.map((d) => (
                          <option key={d.id} value={`${DEPT_PREFIX}${d.id}`}>
                            {d.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {jobOptions.length > 0 && (
                      <optgroup label="Per professione">
                        {jobOptions.map((j) => (
                          <option key={j.id} value={`${JOB_PREFIX}${j.id}`}>
                            {j.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </span>

                <span className="competence-search__department user-admin__select">
                  <select
                    id="live-review-type"
                    aria-label="Filtra le skill per tipologia"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
                  >
                    <option value="all">Tutte le tipologie</option>
                    {SKILL_TYPE_VALUES.map((t) => (
                      <option key={t} value={t}>
                        {SKILL_TYPE_LABELS[t]}
                      </option>
                    ))}
                    <option value="none">Senza tipo</option>
                  </select>
                </span>

                <TextInput
                  id="live-review-search"
                  className="competence-search__input"
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cerca una skill…"
                  autoComplete="off"
                  aria-label="Cerca una skill per nome"
                />
              </div>
            </div>

            {visibleSkills.length === 0 ? (
              <p role="status">Nessuna skill corrisponde ai filtri o alla ricerca.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th scope="col">Skill</th>
                    <th scope="col">Il tuo voto</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleSkills.map((skill) => {
                    const current = draft.get(skill.id) ?? null;
                    return (
                      <tr key={skill.id}>
                        <th scope="row">
                          <span className="review-form__skill-label">
                            {skill.name}
                          </span>
                        </th>
                        <td>
                          <span
                            className="user-admin__select skill-list__picker-level"
                            data-empty={current === null || undefined}
                          >
                            <ReviewLivelloSelect
                              value={current}
                              label={`Il tuo voto per ${skill.name}`}
                              disabled={submit.isPending}
                              onChange={(livello) =>
                                assignLivello(skill.id, livello)
                              }
                            />
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            <p className="review-form__hint">
              {!hasContent
                ? 'Assegna almeno un Livello o scrivi una nota per inviare.'
                : !isDirty
                  ? 'Modifica la review per inviare una nuova valutazione.'
                  : votedCount === 0
                    ? 'Verrà inviata la sola nota.'
                    : `${votedCount} skill valutate in questa review.`}
            </p>

            {submit.isSuccess && <Alert tone="success">Review inviata.</Alert>}
            {submit.isError && (
              <Alert tone="error">Impossibile inviare la review. Riprova.</Alert>
            )}
          </div>
        </form>
      )}
    </Modal>
  );
}

export default LiveReviewWizard;
